import type { CreateWitnessCompanyRequest } from '../utils/mappers/witness-company-form-mapper';

import apiClient from '@/axios/app-client';
import { FunctionName } from '@/lib/enums';
import { CompaniesApiResponse } from '@/types/api';

// API endpoint constant
const COMPANIES_ENDPOINT = '/v1/companies';

// Common fields for both customer and supplier
interface BaseCompanyRequest {
  galiAccountCode?: number | null;
  partnerClassification: number;
  formalName: string;
  abbreviationName: string;
  branchDepartment?: string;
  partnerNameKana: string;
  locationZipCodeFirst: number;
  locationZipCodeLast: number;
  locationAddress1: string;
  locationAddress2: string;
  locationAddress3?: string;
  locationPhone: string;
  locationFax: string;
  headquartersAddressZipCodeFirst: number;
  headquartersAddressZipCodeLast: number;
  headquartersAddress1: string;
  headquartersAddress2: string;
  headquartersAddress3?: string;
  departmentContactLists: Array<{
    contactPersonCode: number;
    departmentCode: number;
  }>;
  customerCategory: number;
}

// Customer-specific fields (only common fields)
export type CreateCustomerRequest = BaseCompanyRequest;

// Supplier-specific fields (common + required supplier fields)
export interface CreateSupplierRequest extends BaseCompanyRequest {
  // Required supplier fields
  supplierType: number;
  factoryName?: string;
  factoryLocationZipCodeFirst?: number | null;
  factoryLocationZipCodeLast?: number | null;
  factoryLocation?: string | null;
  factoryJisCertNo?: string;
  factoryCertClassification?: string;
  factoryHighStrengthCertNo?: string;
  qualifiedEngineerConstructionDepartment?: number;
  qualifiedConcreteDiagnostician?: number;
  qualifiedConcreteChiefEngineer?: number;
  qualifiedConcreteEngineer?: number;
  mixerNominalCapacity?: string;
  mixerFormat?: string;
  mixerCapacity?: string;
  mixerCardinality?: number;
  mixerProductionCompany?: string;
  mixerWeighingMethod?: string;
  carrierLarge10t?: number;
  carrierMediumSize8t?: number;
  carrierMediumSize7t?: number;
  carrierMediumSize5t?: number;
  carrierSmall4t?: number;
  carrierM3Type3t?: number;
  carrierM3Type2t?: number;
  contactOfficerLists?: Array<{
    displayOrder?: number;
    division?: string;
    department?: string;
    personName?: string;
    phoneNumber?: string;
    emailAddress?: string;
    fax?: string;
  }> | null;
  aggregateCoarseLists?: Array<{
    displayOrder: number;
    type: string;
    producingArea: string;
    limestoneExists: boolean;
  }> | null;
  aggregateFineLists?: Array<{
    displayOrder: number;
    type: string;
    producingArea: string;
    limestoneExists: boolean;
  }> | null;

  // Optional supplier fields
  industryAssociation?: string;
  other?: string;
  materials?: Array<{
    displayOrder: number;
    cementTypeCode: number;
    cementTypeOther?: string;
    maker: string;
  }> | null;
  partnerAssociations?: Array<{
    displayOrder: number;
    name: string;
  }> | null;
  isCommonCement?: boolean;
}

export interface CreateConstructionCompanyRequest {
  galiAccountCode?: number | null;
  name: string;
  nameAbbreviation: string;
  nameKana: string;
  departmentContactLists: Array<{
    contactPersonCode: number;
    departmentCode: number;
  }>;
}
// Union type for the service method
export type CreateCustomerSupplierRequest = CreateCustomerRequest | CreateSupplierRequest;

interface CustomerAutocompleteItem {
  id: string | number;
  name?: string;
  abbreviationName?: string;
  formalName?: string;
}

interface ConstructionCompanyAutocompleteItem {
  id: string | number;
  name?: string;
  nameAbbreviation?: string;
}

export const companiesService = {
  /**
   * Get companies with pagination and search parameters
   */
  async getCompanies(params: Record<string, unknown> = {}) {
    return apiClient.get<CompaniesApiResponse>(COMPANIES_ENDPOINT, params);
  },

  /**
   * Create a new customer or supplier
   * Supports both customer and supplier registration with unified interface
   */
  async createCustomerSupplier(data: CreateCustomerSupplierRequest) {
    return apiClient.post('/v1/companies/customer-suppliers', data);
  },

  /**
   * Create a new construction company
   * Supports construction company registration with unified interface
   */
  async createConstructionCompany(data: CreateConstructionCompanyRequest) {
    return apiClient.post('/v1/companies/construction-company', data);
  },

  /**
   * Create a new witness company
   * Supports witness company registration
   */
  async createWitnessCompany(data: CreateWitnessCompanyRequest) {
    return apiClient.post('/v1/companies/witness-company', data);
  },

  /**
   * Get construction company by ID
   */
  async getConstructionCompanyById(id: string) {
    return apiClient.get(`/v1/companies/construction-company/${id}`);
  },

  /**
   * Update  construction company by ID
   */
  async updateConstructionCompanyById(id: string, data: Partial<CreateConstructionCompanyRequest>) {
    return apiClient.put(`/v1/companies/construction-company/${id}`, data);
  },

  /**
   * Get witness company by ID
   */
  async getWitnessCompanyById(id: string) {
    return apiClient.get(`/v1/companies/witness-company/${id}`);
  },

  /**
   * Update  witness company by ID
   */
  async updateWitnessCompanyById(id: string, data: Partial<CreateWitnessCompanyRequest>) {
    return apiClient.put(`/v1/companies/witness-company/${id}`, data);
  },

  /**
   * Get company by ID
   */
  async getCompanyById(id: string) {
    return apiClient.get(`${COMPANIES_ENDPOINT}/${id}`);
  },

  /**
   * Get customer by ID
   */
  async getCustomerById(id: string) {
    return apiClient.get(`/v1/companies/customer-suppliers/${id}?partnerClassification=1`);
  },

  /**
   * Get supplier by ID
   */
  async getSupplierById(id: string) {
    return apiClient.get(`/v1/companies/customer-suppliers/${id}?partnerClassification=2`);
  },

  /**
   * Update customer by ID
   */
  async updateCustomerById(id: string, data: Partial<CreateCustomerRequest>) {
    return apiClient.put(`/v1/companies/customer-suppliers/${id}`, {
      ...data,
      partnerClassification: 1,
    });
  },

  /**
   * Update supplier by ID
   */
  async updateSupplierById(id: string, data: Partial<CreateSupplierRequest>) {
    return apiClient.put(`/v1/companies/customer-suppliers/${id}`, {
      ...data,
      partnerClassification: 2,
    });
  },

  /**
   * Update company
   */
  async updateCompany(id: string, data: Partial<CreateCustomerSupplierRequest>) {
    return apiClient.put(`${COMPANIES_ENDPOINT}/${id}`, data);
  },

  /**
   * Delete construction company
   */
  async deleteConstructionCompany(id: string) {
    return apiClient.delete(`${COMPANIES_ENDPOINT}/construction-company/${id}`);
  },

  /**
   * Delete witness company
   */
  async deleteWitnessCompany(id: string) {
    return apiClient.delete(`${COMPANIES_ENDPOINT}/witness-company/${id}`);
  },

  /**
   * Delete customer
   */
  async deleteCustomer(id: string) {
    return apiClient.delete(
      `${COMPANIES_ENDPOINT}/customer-suppliers/${id}?partnerClassification=1`
    );
  },

  /**
   * Delete supplier
   */
  async deleteSupplier(id: string) {
    return apiClient.delete(
      `${COMPANIES_ENDPOINT}/customer-suppliers/${id}?partnerClassification=2`
    );
  },

  /**
   * Get companies by partner classification
   */
  async getCompaniesByPartnerClassification(
    partnerClassification: number,
    functionName: FunctionName
  ) {
    return apiClient.get(
      `${COMPANIES_ENDPOINT}/${partnerClassification}?functionName=${functionName}`
    );
  },

  /**
   * Get customers (partnerClassification=1) for autocomplete
   */
  async getCustomersForAutocomplete(functionName: FunctionName, query: string = '') {
    const response = await this.getCompaniesByPartnerClassification(1, functionName);
    const customers: CustomerAutocompleteItem[] = Array.isArray(response.data.data)
      ? (response.data.data as CustomerAutocompleteItem[])
      : [];

    // Filter by query if provided
    const filtered = query
      ? customers.filter(
          (customer) =>
            customer.abbreviationName?.toLowerCase().includes(query.toLowerCase()) ||
            customer.formalName?.toLowerCase().includes(query.toLowerCase())
        )
      : customers;

    // Transform to SearchOption format
    return filtered.map((customer) => ({
      value: customer.id,
      label: customer.name,
      // Store full data for additional info
      data: customer,
    }));
  },

  /**
   * Get construction companies (partnerClassification=3) for autocomplete
   */
  async getConstructionCompaniesForAutocomplete(functionName: FunctionName, query: string = '') {
    const response = await this.getCompaniesByPartnerClassification(3, functionName);
    const companies: ConstructionCompanyAutocompleteItem[] = Array.isArray(response.data.data)
      ? (response.data.data as ConstructionCompanyAutocompleteItem[])
      : [];

    // Filter by query if provided
    const filtered = query
      ? companies.filter(
          (company) =>
            company.nameAbbreviation?.toLowerCase().includes(query.toLowerCase()) ||
            company.name?.toLowerCase().includes(query.toLowerCase())
        )
      : companies;

    // Transform to SearchOption format
    return filtered.map((company) => ({
      value: company.id,
      label: company.name,
      // Store full data for additional info
      data: company,
    }));
  },
};
